import { inflect } from 'inflection';
import _ from 'lodash';
import React, { useRef } from 'react';
import { useDragLayer } from 'react-dnd';
import SVGIcon from '../components/SVGIcon';
import moveIcon from '../icons/move.svg';
import memoizeDndCollect from '../utils/memoizeDndCollect';
import styles from './ContentMapDragLayer.module.scss';
import { DragTypes } from './useTopicReorganization';

const distance = ({ x: x1, y: y1 }, { x: x2, y: y2 }) =>
  Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

const collect = (itemRef) => {
  let lastFewOffsets = [];
  let interval;
  const startInterval = () => {
    interval = setInterval(() => {
      if (lastFewOffsets.length > 1) {
        lastFewOffsets.shift();
      } else {
        clearInterval(interval);
      }
    }, 16);
  };

  const updateTransform = _.throttle((newOffset) => {
    clearInterval(interval);
    if (newOffset) {
      startInterval();

      if (
        lastFewOffsets.length >= 2 &&
        distance(lastFewOffsets[lastFewOffsets.length - 1], newOffset) >
          distance(lastFewOffsets[lastFewOffsets.length - 2], newOffset)
      ) {
        lastFewOffsets.pop();
      }
      lastFewOffsets.push(newOffset);
      if (lastFewOffsets.length > 3) {
        lastFewOffsets.shift();
      }

      const offset = lastFewOffsets[0];
      const transform = `translate(${offset.x}px, ${offset.y}px)`;
      itemRef.current.style.setProperty('-webkit-transform', transform);
      itemRef.current.style.setProperty('transform', transform);
    } else {
      lastFewOffsets = [];
      itemRef.current.style.setProperty('display', 'none');
    }
  });

  return memoizeDndCollect((monitor) => {
    if (itemRef.current) {
      updateTransform(
        monitor.getSourceClientOffset() || monitor.getInitialClientOffset(),
      );
    }

    return {
      item: monitor.getItem(),
      isDragging: monitor.isDragging(),
    };
  });
};

const renderItem = (item) => {
  let description;
  switch (item.type) {
    case DragTypes.topics:
    case DragTypes.column: {
      const count = item.toMove.topicIds.length;
      description = `${count} ${inflect('topic', count)}`;
      break;
    }
    case DragTypes.pinnedPosts: {
      const count = item.toMove.pinnedPostKeys.length;
      description = `${count} ${inflect('pinned post', count)}`;
      break;
    }
    case DragTypes.posts:
    case DragTypes.mixed:
      description = Object.entries({
        topic: item.toMove.topicIds.length,
        post: item.toMove.pinnedPostKeys.length + item.toMove.postKeys.length,
      })
        .filter(([, count]) => count > 0)
        .map(([noun, count]) => `${count} ${inflect(noun, count)}`)
        .join(' and ');
      break;
    default:
      return null;
  }

  return (
    <div className={styles.move}>
      <SVGIcon className={styles.moveIcon} inline>
        {moveIcon}
      </SVGIcon>
      <span className={styles.moveText}>Move</span> {description}
    </div>
  );
};

const ContentMapDragLayer = () => {
  const itemRef = useRef(null);
  const { item, isDragging } = useDragLayer(collect(itemRef));

  if (!isDragging) {
    return null;
  }

  const rendered = renderItem(item);
  if (!rendered) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.item} ref={itemRef}>
        {rendered}
      </div>
    </div>
  );
};

export default ContentMapDragLayer;
